import React, { useEffect, useRef, useState } from 'react';
import Button from './Button';

/**
 * A button that asks before it acts: the first click swaps it for the prompt,
 * a confirm button and Cancel, in place. Esc or Cancel goes back.
 * Remaining props go to the first button.
 */
export default function ConfirmButton({ prompt, confirmLabel, onConfirm, busy = false, busyLabel, children, ...buttonProps }) {
  const [asking, setAsking] = useState(false);
  const confirmRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  useEffect(() => {
    if (asking) confirmRef.current?.focus();
  }, [asking]);

  if (!asking && !busy) {
    return (
      <Button {...buttonProps} onClick={() => setAsking(true)}>
        {children}
      </Button>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-2"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) {
          event.preventDefault();
          event.stopPropagation();
          setAsking(false);
        }
      }}
    >
      <span className="text-body text-ink whitespace-nowrap">{prompt}</span>
      <button
        ref={confirmRef}
        type="button"
        disabled={busy}
        onClick={async () => {
          await onConfirm();
          if (mounted.current) setAsking(false);
        }}
        className="inline-flex items-center h-7 px-2.5 rounded-control border border-crit bg-crit text-small font-semibold text-sheet hover:bg-crit/90 disabled:opacity-60"
      >
        {busy ? busyLabel || confirmLabel : confirmLabel}
      </button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setAsking(false)}>
        Cancel
      </Button>
    </span>
  );
}
